import CardHeader from "components/CardHeader";
import PageMeta from "components/PageMeta";
import FilterToggleButton from "components/FilterToggleButton";
import usePersistentFilterSet from "common/usePersistentFilterSet";
import { Account, AccountRole } from "models/Account";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import * as api from "services/Api";
import { useOfficeTranslation } from "i18n/OfficeTranslation";

const AccountRoles: AccountRole[] = ["customer", "supplier"];
type AccountRoleFilter = AccountRole | "unassigned";
const AccountRoleFilters: AccountRoleFilter[] = [...AccountRoles, "unassigned"];

const Accounts: React.FC = () => {
    const { t } = useOfficeTranslation();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [query, setQuery] = useState("");
    const [visibleRoles, toggleRole] = usePersistentFilterSet(
        "galactic-filters-accounts-roles-v2",
        AccountRoleFilters,
    );
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getAccounts()
            .then(setAccounts)
            .finally(() => setLoading(false));
    }, []);

    const matchingAccounts = useMemo(
        () => accounts.filter(account =>
            !query.trim() || [
                account.legal_name,
                account.email,
                account.vat_number,
                ...(account.trade_names ?? []),
            ].some(value => value?.toLowerCase().includes(query.toLowerCase())),
        ),
        [accounts, query],
    );
    const roleCounts = useMemo(
        () => matchingAccounts.reduce<Record<AccountRoleFilter, number>>(
            (counts, account) => {
                const roles = account.roles ?? [];
                if (roles.length === 0) {
                    counts.unassigned += 1;
                } else {
                    roles.forEach(role => counts[role] += 1);
                }
                return counts;
            },
            { customer: 0, supplier: 0, unassigned: 0 },
        ),
        [matchingAccounts],
    );
    const filtered = useMemo(
        () => matchingAccounts.filter(account =>
            (account.roles ?? []).length === 0
                ? visibleRoles.has("unassigned")
                : (account.roles ?? []).some(role => visibleRoles.has(role)),
        ),
        [matchingAccounts, visibleRoles],
    );

    return (
        <div className="page-content accounts-page">
            <PageMeta title={t("common.accounts")} />

            <div className="page-title-box accounts-page-title d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                    <h4 className="mb-1">{t("common.accounts")}</h4>
                    <p className="text-muted mb-0">{t("accounts.accounts.customersAndSuppliersInOneRelationshipOverview")}</p>
                </div>
                <Link className="btn btn-primary account-add-button" to="/accounts/add">
                    <i className="mdi mdi-plus me-1" />
                    <span>{t("common.newAccount")}</span>
                </Link>
            </div>

            <Card className="accounts-list-card">
                <CardBody className="accounts-list-card-body">
                    <div className="accounts-list-header">
                        <CardHeader
                            title={t("accounts.accounts.accountOverview")}
                            icon="mdi-domain"
                            count={filtered.length}
                        />
                    </div>

                    <div className="accounts-toolbar">
                        <div className="account-search-field">
                            <i className="mdi mdi-magnify" aria-hidden="true" />
                            <input
                                aria-label={t("accounts.accounts.searchAccounts")}
                                className="form-control"
                                placeholder={t("accounts.accounts.searchNameEmailOrVatNumber")}
                                value={query}
                                onChange={event => setQuery(event.target.value)}
                            />
                        </div>
                        <div className="account-role-filters">
                            <FilterToggleButton
                                active={visibleRoles.has("customer")}
                                count={roleCounts.customer}
                                label={t("common.customers")}
                                onToggle={() => toggleRole("customer")}
                            />
                            <FilterToggleButton
                                active={visibleRoles.has("supplier")}
                                count={roleCounts.supplier}
                                label={t("accounts.accounts.suppliers")}
                                onToggle={() => toggleRole("supplier")}
                            />
                            <FilterToggleButton
                                active={visibleRoles.has("unassigned")}
                                count={roleCounts.unassigned}
                                label={t("accounts.accounts.withoutRole")}
                                onToggle={() => toggleRole("unassigned")}
                            />
                        </div>
                    </div>

                    {loading && <p className="text-muted text-center py-5 mb-0">{t("accounts.accounts.loadingAccounts")}</p>}

                    {!loading && filtered.map(account => {
                        const addresses = account.addresses ?? [];
                        const roles = account.roles ?? [];
                        const tradeNames = account.trade_names ?? [];
                        const address = addresses.find(item => item.primary) ?? addresses[0];

                        return (
                            <Link
                                aria-label={`${t("common.open")}: ${account.legal_name}`}
                                className="account-list-item text-body text-decoration-none"
                                key={account.id}
                                to={`/accounts/${account.id}`}
                            >
                                <div className="account-list-identity">
                                    <div className="account-list-title-row">
                                        <strong title={account.legal_name}>{account.legal_name}</strong>
                                        <div className="account-list-badges">
                                            {roles.length === 0 &&
                                                <span className="badge bg-secondary bg-soft text-secondary">
                                                    {t("accounts.accounts.withoutRole")}
                                                </span>
                                            }
                                            {roles.map(item =>
                                                <span
                                                    className={`badge ${item === "customer"
                                                        ? "bg-primary bg-soft text-primary"
                                                        : "bg-warning bg-soft text-warning"
                                                    }`}
                                                    key={item}
                                                >
                                                    {t(item === "customer" ? "common.roles.customer" : "common.roles.supplier")}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {tradeNames.length > 0 &&
                                        <small title={tradeNames.join(", ")}>
                                            {tradeNames.join(", ")}
                                        </small>
                                    }
                                </div>

                                <div className="account-list-status">
                                    {account.pending_attachment_count > 0 &&
                                        <span className="badge bg-warning bg-soft text-warning">
                                            <i className="mdi mdi-file-clock-outline me-1" aria-hidden="true" />
                                            {t(account.pending_attachment_count === 1
                                                ? "accounts.accounts.pendingDocument"
                                                : "accounts.accounts.pendingDocuments", {
                                                count: account.pending_attachment_count,
                                            })}
                                        </span>
                                    }
                                    {account.attachment_count > 0 &&
                                        account.pending_attachment_count === 0 &&
                                        account.approved_attachment_count === account.attachment_count &&
                                        <span className="badge bg-success bg-soft text-success">
                                            <i className="mdi mdi-file-check-outline me-1" aria-hidden="true" />
                                            {t(account.attachment_count === 1
                                                ? "accounts.accounts.approvedDocument"
                                                : "accounts.accounts.approvedDocuments", {
                                                approved: account.approved_attachment_count,
                                                total: account.attachment_count,
                                            })}
                                        </span>
                                    }
                                </div>

                                <div className="account-list-contact">
                                    <span title={account.email || t("common.noEmail")}>
                                        <i className="mdi mdi-email-outline" aria-hidden="true" />
                                        {account.email || <span className="text-muted">{t("common.noEmail")}</span>}
                                    </span>
                                    {account.phone &&
                                        <span title={account.phone}>
                                            <i className="mdi mdi-phone-outline" aria-hidden="true" />
                                            {account.phone}
                                        </span>
                                    }
                                    <span title={address?.city || t("accounts.accounts.unknownLocation")}>
                                        <i className="mdi mdi-map-marker-outline" aria-hidden="true" />
                                        {address?.city || <span className="text-muted">{t("accounts.accounts.unknownLocation")}</span>}
                                    </span>
                                </div>

                                <span className="account-list-open" aria-hidden="true">
                                    <i className="mdi mdi-chevron-right" />
                                </span>
                            </Link>
                        );
                    })}

                    {!loading && !filtered.length &&
                        <p className="text-muted text-center mt-3 mb-0">{t("common.noAccountsFound")}</p>
                    }
                </CardBody>
            </Card>
        </div>
    );
};

export default Accounts;
